import { formatInTimeZone } from 'date-fns-tz';
import { memo, useEffect, useRef } from 'react';

import { cn } from '@/ui/lib/utils';

import { minutesFromDayStart, startOfDayInTimeZone } from '../../utils/dateGrid';
import AllDayRow from '../AllDayRow';
import DayColumn from '../DayColumn';
import TimeGutter from '../TimeGutter';

import type { TimeGridProps } from './types';

function TimeGrid({
  days,
  events,
  minMinutes,
  maxMinutes,
  pxPerMinute,
  timeZone,
  snapToMinutes,
  isDateHighlighted,
  onEventClick,
  onSlotClick,
  onDayHeaderClick,
  renderEventContent,
  renderEventPopover
}: TimeGridProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const now = new Date();
  const nowMinutes = minutesFromDayStart(now, startOfDayInTimeZone(now, timeZone), timeZone);
  const nowAnchorTop =
    (Math.min(Math.max(nowMinutes, minMinutes), maxMinutes) - minMinutes) * pxPerMinute;

  // Center the current time of day on mount so users don't land at midnight. Scrolling the
  // grid's own container keeps the page toolbar and sticky day headers in view.
  useEffect(() => {
    const container = scrollContainerRef.current;
    const body = bodyRef.current;
    if (!container || !body) return;
    container.scrollTop = body.offsetTop + nowAnchorTop - container.clientHeight / 2;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={scrollContainerRef} className="relative flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="sticky top-0 z-20 flex flex-col bg-gray-100 dark:bg-uniform-gray-900">
        <div className="flex">
          <div className="w-14 shrink-0" />
          <div
            className="grid flex-1"
            style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
            {days.map((day) => (
              <div
                key={day.toISOString()}
                onClick={() => onDayHeaderClick?.(day)}
                className={cn(
                  'border-l border-uniform-gray-100 py-1 text-center text-xs font-medium text-uniform-gray-600 first:border-l-0 dark:border-uniform-gray-800 dark:text-uniform-gray-300',
                  onDayHeaderClick &&
                    'cursor-pointer hover:text-uniform-gray-900 dark:hover:text-uniform-gray-100'
                )}>
                {formatInTimeZone(day, timeZone, days.length > 1 ? 'EEE d' : 'EEEE, MMM d')}
              </div>
            ))}
          </div>
        </div>

        <div className="flex">
          <div className="w-14 shrink-0" />
          <AllDayRow
            className="flex-1"
            days={days}
            events={events}
            timeZone={timeZone}
            onEventClick={onEventClick}
            onSlotClick={onSlotClick}
            renderEventContent={renderEventContent}
            renderEventPopover={renderEventPopover}
          />
        </div>
      </div>

      <div ref={bodyRef} className="flex">
        <TimeGutter
          minMinutes={minMinutes}
          maxMinutes={maxMinutes}
          pxPerMinute={pxPerMinute}
          timeZone={timeZone}
        />
        <div className="flex flex-1">
          {days.map((day) => (
            <DayColumn
              key={day.toISOString()}
              day={day}
              events={events}
              minMinutes={minMinutes}
              maxMinutes={maxMinutes}
              pxPerMinute={pxPerMinute}
              timeZone={timeZone}
              snapToMinutes={snapToMinutes}
              isHighlighted={isDateHighlighted?.(day)}
              showCurrentTimeIndicator
              onEventClick={onEventClick}
              onSlotClick={onSlotClick}
              renderEventContent={renderEventContent}
              renderEventPopover={renderEventPopover}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default memo(TimeGrid);
